/**
 * InvoicePrintView — the customer-facing, print-ready install invoice (chrome-free
 * route /jobs/:jobId/invoice, opened in its own tab → browser "Save as PDF" = a
 * crisp vector PDF). Sedgwick-branded (NOT Pegasus). Reads the same job records the
 * in-app preview uses; print-aware (materials toggled off, e.g. refrigerant, never
 * show). Costs/part numbers stay internal — the customer sees a flat Total Contract
 * Price + the equipment block + a plain-English "what's included" list.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, X, CheckCircle2, Heart, HandCoins, Mail } from "lucide-react";
// PAY-02 entry point: collect a payment right from the invoice document.
import { CollectPaymentModal } from "@/features/payments/CollectPaymentModal";
import { SendInvoiceEmailModal } from "@/features/comms/SendInvoiceEmailModal";
import { useInvoiceSendStatuses, InvoiceSentChip } from "@/features/comms/invoiceSend";
import { useCustomerPayments, invoicePaidMap, docPaymentLines } from "@/features/payments/payments";
import { useJobMoney } from "./installField";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { supabase } from "@/lib/supabase";
import { useInvoiceConfig, type InvoiceConfig } from "./invoiceConfig";
import {
  useJob, useJobEquipment, useJobMaterials, useJobLabor, useJobInvoice, useJobInvoices, useInvoiceDoc, useChangeOrders, useInvoiceLineEdits, useJobVisits, manufacturerLabel, type Job, type InvoiceDoc,
} from "./jobs";
import { useInvoice, useInvoiceLines, useInvoiceTechs } from "@/features/invoice-detail/api";
import { classifyEquipment, parseWarranty, type EquipmentRow } from "@/lib/equipment";

// Company header + warranty terms come from Settings → Invoices (invoiceConfig).

const money = (n: number | null | undefined) =>
  n == null ? "$0.00" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

function warranty(category: string | null, installISO: string, kind: "parts" | "labor", cfg: InvoiceConfig): string {
  const isControl = /(stat|thermostat|control)/i.test(category ?? "");
  const yrs = kind === "labor" ? cfg.warrantyLabor : (isControl ? cfg.warrantyPartsControl : cfg.warrantyPartsEquip);
  const d = new Date(installISO.length <= 10 ? installISO + "T00:00:00" : installISO);
  d.setFullYear(d.getFullYear() + yrs);
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}

// A collected payment — method + reference (card auth, check #, cash, or financing plan).
interface Payment { method: string; reference: string; amount: number }
interface ServiceLine { qty: number; desc: string; price: number; waived?: boolean }
// Example service-call data so the Service invoice can be previewed at ?type=service.
// Real service invoices will read itemized lines + tech notes + payment from the visit.
// HARD RULE: SARES / SASALES agreement reserve-balance lines are NEVER shown here or on
// any Pegasus invoice (Seth).
const SERVICE_SAMPLE = {
  lines: [
    { qty: 1, desc: "Diagnostic Charge", price: 79 },
    { qty: 1, desc: "SPP6 Hard Start Kit 500%", price: 191 },
  ] as ServiceLine[],
  techNotes:
    "System not cooling on arrival — condenser fan running, compressor not starting. Compressor windings tested OK; capacitor in range. Installed a hard-start kit; compressor now starts and pulls 5.99 / 6.03 running amps per leg. R-410A pressures 117 / 285 psig, 8.4°F subcool, ODT 71°F. System cooling on departure. Condenser is dirty — recommend a cleaning.",
  payment: { method: "Visa", reference: "•••• 7545 · Auth 000142", amount: 270 } as Payment,
};

interface OwnerBillTo { fullname: string | null; add1: string | null; add2: string | null; city: string | null; state: string | null; zip: string | null; phone1: string | null }
function useOwnerBillTo(custno: string | null) {
  return useQuery({
    queryKey: ["owner_billto", custno],
    enabled: !!custno,
    queryFn: async (): Promise<OwnerBillTo | null> => {
      const { data } = await supabase.from("mirror_customer")
        .select("fullname, add1, add2, city, state, zip, phone1").eq("custno", custno!).maybeSingle();
      return (data ?? null) as OwnerBillTo | null;
    },
  });
}
/** "Last,First 1234" → "First Last" */
function cleanName(fullname: string | null): string | null {
  if (!fullname) return null;
  let s = fullname.replace(/\s+\d{4,}\s*$/, "").trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) s = `${m[2]} ${m[1]}`.trim();
  return s || null;
}

// The SERVICE LOCATION (where the work happened) — mirror_location by custno+locno.
// Distinct from the Bill-To (mirror_customer / owner). In docMode the synthetic job
// carried no address, so the printed invoice showed a blank Service Location — this
// is the real address.
interface SvcLoc { locname: string | null; add1: string | null; city: string | null; state: string | null; zip: string | null }
function useServiceLocation(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["svc_loc_print", custno, locno],
    enabled: !!custno,
    queryFn: async (): Promise<SvcLoc | null> => {
      let q = supabase.from("mirror_location").select("locname, add1, city, state, zip").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.limit(1).maybeSingle();
      return (data ?? null) as SvcLoc | null;
    },
  });
}

// ----------------------------------------------------------------------------
// INV-SERIAL-01 (Seth 2026-10-01: "the serial numbers for the equipment are
// not listed… displayed properly in the way it makes the most sense and looks
// nice"). The industry standard (ServiceTitan et al.) is a dedicated
// equipment block — model + serial per unit — because that's what warranty
// registration, insurance and inspectors ask for, and it keeps the priced
// What's Included table clean. ESC invoices: mirror_equipment rows keyed by
// this invoice number. Pegasus invoices: pegasus_equipment rows on the job.
// Self-hides when the invoice installed nothing.
// ----------------------------------------------------------------------------
function useEscInvoiceEquipment(escNo: string | null) {
  return useQuery({
    queryKey: ["esc_invoice_equipment", escNo],
    enabled: !!escNo,
    queryFn: async (): Promise<EquipmentRow[]> => {
      const { data } = await supabase
        .from("mirror_equipment")
        .select("counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, locno, custom_1, custom_2, invoice")
        .eq("invoice", escNo!)
        .limit(80);
      return (data ?? []) as EquipmentRow[];
    },
  });
}

/** "LENNOX" → "Lennox", "AO SMITH" → "AO Smith" — brand case fit for print. */
function prettyBrand(s: string | null): string {
  return (s ?? "").trim().split(/\s+/).map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join(" ");
}

// Comfort Advisor / salesperson is stored as an ESC employee number on older records
// (e.g. "0026"); resolve it to a real name for the customer-facing invoice. New Pegasus
// jobs already store the name, so this only fires when the value looks like an empno.
interface EmpName { firstname: string | null; lastname: string | null; empname: string | null }
function useEmpName(code: string | null) {
  const trimmed = (code ?? "").trim();
  return useQuery({
    queryKey: ["inv_emp_name", trimmed],
    enabled: /^\d+$/.test(trimmed),
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.from("mirror_employee").select("firstname, lastname, empname").eq("empno", trimmed).maybeSingle();
      const e = data as EmpName | null;
      if (!e) return null;
      const full = [e.firstname, e.lastname].filter(Boolean).join(" ").trim();
      return full || e.empname || null;
    },
  });
}

export function InvoicePrintView() {
  const { jobId, invoiceId, escNo } = useParams();
  const { data: realJob, isLoading } = useJob(jobId ?? null);
  const { data: pegasusDoc, isLoading: pegLoading } = useInvoiceDoc(invoiceId ?? null);
  // INVOICE-LIVE-01: the printed invoice follows the tech's iPad live.
  useRealtimeRefresh(["pegasus_job_invoice", "pegasus_job_invoice_line"], [["invoice_doc"]]);
  const { data: escInv, isLoading: escLoadingH } = useInvoice(escNo);
  const { data: escLines } = useInvoiceLines(escNo);
  const { data: escTechs } = useInvoiceTechs(escNo);
  // A finalized install invoice viewed by its own ID renders from its (now read-only) job,
  // so the durable record looks identical to the workspace print — not a thin snapshot.
  const effJobId = jobId ?? pegasusDoc?.job_id ?? null;
  // NC-DRAW-02: a draw is billed to the builder for ONE house — the house is
  // the service location (a new lot has no ESC location).
  const { data: drawJob } = useJob(pegasusDoc && (pegasusDoc as { type?: string | null }).type === "draw" ? pegasusDoc.job_id : null);
  const { data: equip } = useJobEquipment(effJobId);
  const { data: mats } = useJobMaterials(effJobId);
  const { data: changeOrders } = useChangeOrders(effJobId);
  const approvedCOs = (changeOrders ?? []).filter((c) => c.status === "approved");
  // Invoice customization overlay (show/hide + custom lines) — same as the on-screen preview.
  const { data: lineEdits } = useInvoiceLineEdits(effJobId);
  const hiddenKeys = new Set((lineEdits ?? []).filter((e) => e.line_key && e.hidden).map((e) => e.line_key as string));
  const customLines = (lineEdits ?? []).filter((e) => e.is_custom);
  const customAddl = customLines.reduce((s, c) => s + Number(c.amount || 0), 0);
  const visEquip = (equip ?? []).filter((e) => !hiddenKeys.has(`equip:${e.id}`));
  const visCOs = approvedCOs.filter((c) => !hiddenKeys.has(`co:${c.id}`));
  const coAddl = visCOs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const editByKey = new Map((lineEdits ?? []).filter((e) => e.line_key).map((e) => [e.line_key as string, e]));
  const labelFor = (key: string, derived: string) => editByKey.get(key)?.label ?? derived;
  const { data: labor } = useJobLabor(jobId ?? null);
  const { data: inv } = useJobInvoice(jobId ?? null);
  const { data: owner } = useOwnerBillTo((invoiceId ? pegasusDoc?.custno : escNo ? escInv?.custno : realJob?.custno) ?? null);
  const svcCustno = (invoiceId ? pegasusDoc?.custno : escNo ? escInv?.custno : realJob?.custno) ?? null;
  const svcLocno = (invoiceId ? pegasusDoc?.locno : escNo ? escInv?.locno : realJob?.locno) ?? null;
  const { data: svcLoc } = useServiceLocation(svcCustno, svcLocno);
  // INV-SERIAL-01: the units this ESC invoice installed (serials + real warranty dates).
  const { data: escEquipRaw } = useEscInvoiceEquipment(escNo ?? null);
  // Comfort Advisor name (resolve an ESC empno like "0026" → the actual person).
  const advisorCode = (invoiceId ? pegasusDoc?.comfort_advisor : escNo ? escInv?.salesman_name : realJob?.sale_tech) ?? null;
  const { data: advisorName } = useEmpName(advisorCode);
  // Installer on a JOB invoice = the lead installer on the install visit (matches the
  // board + the on-screen preview); labor hours are the fallback.
  const { data: jobVisits } = useJobVisits(realJob?.id ?? null);
  const leadInstallerEmpno = (jobVisits ?? []).find((v) => v.visit_role === "install" && v.assigned_empno)?.assigned_empno ?? null;
  const { data: leadInstallerName } = useEmpName(leadInstallerEmpno);
  const [bw, setBw] = useState(false); // Color / B&W (laser-printer-friendly) print mode
  // The S mark (public/sedgwick-mark.png) — hides gracefully until the file
  // exists, so the type-only header never shows a broken image.
  const [markOk, setMarkOk] = useState(true);

  const [searchParams] = useSearchParams();
  // PAY-02: "money actions live where money is visible" — a Pegasus invoice with an
  // open balance can take a payment right here. ESC history stays read-only (AR mirror).
  // FIELD-PAY-01: the job's print IS the final invoice — before close that invoice
  // doesn't exist yet (the newest row can be the deposit), so only an install
  // invoice counts here; before close the money comes from the job itself.
  const finalInv = inv && (inv as { type?: string | null }).type === "install" ? inv : null;
  const payInvoice = escNo
    ? null
    : invoiceId
      ? (pegasusDoc ? { id: pegasusDoc.id, custno: (pegasusDoc as { custno?: string | null }).custno ?? null, total: Number(pegasusDoc.total ?? 0), type: (pegasusDoc as { type?: string | null }).type ?? null } : null)
      : (finalInv ? { id: finalInv.id, custno: (finalInv as { custno?: string | null }).custno ?? null, total: Number(finalInv.total ?? 0), type: "install" } : null);
  // A deposit invoice is voided when the job closes — its payment moved onto the
  // final invoice. It shows what was paid, owes nothing, and can't be collected on.
  const isVoidDoc = !!invoiceId && (pegasusDoc as { status?: string | null } | null)?.status === "void";
  // Before close (the job's own print, no final invoice yet): the deposit
  // invoice(s) + money held for the job are this job's payments so far.
  const preClose = !escNo && !invoiceId && !!jobId && !finalInv && searchParams.get("type") !== "service";
  const { data: jobInvs } = useJobInvoices(preClose ? (jobId ?? null) : null);
  const depositIds = new Set((jobInvs ?? []).filter((i) => (i as { type?: string | null }).type === "deposit" && (i as { status?: string | null }).status !== "void").map((i) => i.id));
  const canCollect = useCan("payments.collect");
  const { data: payLedger } = useCustomerPayments(payInvoice?.custno ?? (preClose ? realJob?.custno ?? null : null));
  const payBalance = payInvoice && !isVoidDoc ? Math.max(0, payInvoice.total - (invoicePaidMap(payLedger).get(payInvoice.id) ?? 0)) : 0;
  // Financing approved but not funded yet: the lender pays that part — the
  // document says so, so nobody pays it twice.
  const { data: jobMoney } = useJobMoney(escNo ? null : (jobId ?? pegasusDoc?.job_id ?? null));
  const [showCollect, setShowCollect] = useState(false);
  // COMMS: email-this-invoice — works for Pegasus invoices (ledger balance) AND
  // ESC history (AR-mirror balance). Goes through the outbox (held in testing).
  const [showEmail, setShowEmail] = useState(false);
  const emailInvoice = payInvoice
    ? {
        id: payInvoice.id,
        custno: payInvoice.custno,
        total: payInvoice.total,
        balance: payBalance,
        defaultEmail: (pegasusDoc as { recipient_email?: string | null } | null)?.recipient_email ?? null,
      }
    : escNo && escInv
      ? {
          id: `esc:${escNo}`,
          custno: escInv.custno,
          total: Number(escInv.inv_amount ?? 0),
          balance: Math.max(0, Number(escInv.rec_inv_amt ?? escInv.inv_amount ?? 0) - Number(escInv.rec_paid ?? 0)),
          defaultEmail: null,
        }
      : null;
  // INVOICE-SENT-01: what the outbox says about THIS invoice (the tech's send
  // on Complete or the office's) — shown beside the Email button; and
  // /invoices/<id>?email=1 (the Invoice List / Today "Email it") opens the
  // email modal straight away, once.
  const sendStatus = useInvoiceSendStatuses([emailInvoice?.id]).data?.get(emailInvoice?.id ?? "");
  const cfg = useInvoiceConfig();
  const navigate = useNavigate();
  const emailParamHandled = useRef(false);
  useEffect(() => {
    if (emailParamHandled.current || !emailInvoice || searchParams.get("email") !== "1") return;
    emailParamHandled.current = true;
    setShowEmail(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailInvoice?.id, searchParams]);
  // Close reliably: if this opened in its own tab (via window.open), close it; otherwise
  // go back to wherever you were in Pegasus (or the jobs list as a floor).
  const closeDoc = () => {
    if (window.opener && !window.opener.closed) window.close();
    else if (window.history.length > 1) navigate(-1);
    else navigate("/jobs");
  };
  // Dynamic fit-to-ONE-page on print (Seth 2026-07-01). Just before printing, measure the
  // sheet; only if it would spill past one page do we scale it down with `zoom` (which
  // reflows, so the page break respects the smaller size). Short invoices print full-size;
  // a long one shrinks just enough to stay on one page. Plain zoom only — the paper is
  // left-aligned (margin:0), so any shrink leaves a little white on the right and NEVER
  // clips content (an earlier width-counter-scale overflowed the page and clipped it).
  const paperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fit = () => {
      const el = paperRef.current; if (!el) return;
      el.style.zoom = "1";
      const usable = 10.6 * 96; // printable Letter height (full-bleed margin:0, small printer-safe inset)
      const h = el.scrollHeight;
      el.style.zoom = h > usable ? String(Math.max(0.6, usable / h)) : "1";
    };
    const reset = () => { const el = paperRef.current; if (el) el.style.zoom = "1"; };
    window.addEventListener("beforeprint", fit);
    window.addEventListener("afterprint", reset);
    return () => { window.removeEventListener("beforeprint", fit); window.removeEventListener("afterprint", reset); };
  }, []);

  const docMode = !!invoiceId || !!escNo;
  // Unified invoice doc: Pegasus-native (install/service) OR an ESC historical invoice
  // mapped into the SAME shape, so any invoice prints in the customer format (its real
  // ESC number preserved). SARES/SASALES + no-print + the notes line are filtered out.
  // Tender → settled payment method + amount, from ESC's amt_* split (already mirrored).
  // amt_charge = put on the customer's account (still owed), so it is NOT a payment.
  // Card brand + auth #/check # arrive later from a tender-detail mirror → paid_reference.
  const escPaidAmt = escInv
    ? Number(escInv.amt_cash ?? 0) + Number(escInv.amt_check ?? 0) + Number(escInv.amt_credit_c ?? 0) + Number(escInv.amt_applied_credits ?? 0)
    : 0;
  const escMethod = !escInv ? null
    : Number(escInv.amt_credit_c ?? 0) > 0 ? "Credit Card"
    : Number(escInv.amt_check ?? 0) > 0 ? "Check"
    : Number(escInv.amt_cash ?? 0) > 0 ? "Cash"
    : Number(escInv.amt_applied_credits ?? 0) > 0 ? "Account Credit"
    : null;
  // True paid status from ESC AR (mirror_receivable) — AUTHORITATIVE, includes payments applied
  // AFTER invoicing (Enter Payments). When present it drives paid/balance; the amt_* tender split
  // only supplies the METHOD label (card/check/cash) for what was collected at the counter.
  const escRecKnown = !!escInv && escInv.rec_inv_amt != null;
  const escRecPaid = Number(escInv?.rec_paid ?? 0);
  const escPaidAmount = escRecKnown ? escRecPaid : escPaidAmt;
  // ESC install signature = a "Total Contract Price" / TOTAL line (service invoices are itemized
  // with no such line). Drives the install-vs-service layout for historical ESC invoices.
  const escHasTotalLine = (escLines ?? []).some((l) => (l.prod ?? "").toUpperCase() === "TOTAL" || /total contract price/i.test(l.line_desc ?? ""));
  const escDoc: InvoiceDoc | null = escNo && escInv ? {
    id: escInv.invoice, type: escHasTotalLine ? "install" : "service", dispatch_no: escInv.dispatch, custno: escInv.custno, locno: escInv.locno,
    job_id: null, job_name: null, comfort_advisor: escInv.salesman_name ?? null,
    technician: (escTechs ?? [])[0]?.name ?? null, completed_date: escInv.inv_date,
    tech_notes: (escLines ?? []).find((l) => !l.no_print && ((l.prod ?? "").toUpperCase() === "DESC" || (l.line_desc ?? "").toLowerCase().startsWith("tech notes")))?.line_desc ?? null,
    paid_method: escPaidAmount > 0 ? escMethod : null, paid_reference: null, paid_amount: escPaidAmount > 0 ? escPaidAmount : null, signature_url: null, po_number: null,
    invoice_no: escInv.invoice, total: escInv.inv_amount, created_at: escInv.inv_date ?? new Date().toISOString(),
    lines: (escLines ?? [])
      .filter((l) => !l.no_print && (l.prod ?? "").toUpperCase() !== "DESC" && !["SARES", "SASALES"].includes((l.prod ?? "").toUpperCase()) && !/^payment/i.test((l.line_desc ?? "").trim()) && ((l.line_desc ?? "").trim() !== "" || (l.amount != null && Number(l.amount) !== 0)))
      .map((l, i) => ({ id: `${l.invoice}-${l.line_count}`, description: l.line_desc, qty: l.quan, unit_price: l.price, amount: l.amount, kind: "service", sort: i, print: true, waived: null })),
  } : null;
  const doc: InvoiceDoc | null = invoiceId ? (pegasusDoc ?? null) : escDoc;
  const docLoading = (!!invoiceId && pegLoading) || (!!escNo && escLoadingH);

  // INV-CUST-01 (Seth 2026-09-30, the R&D Batteries $57,600 close): ESC
  // install invoices ARE the line story — Seth writes them as sections
  // ("Main Floor System:" → the equipment under it at $0, included in the
  // contract price). Until now the customer copy showed an install-type ESC
  // invoice with NO breakdown at all (the Work Performed table was
  // service-only, and the equipment/materials blocks are Pegasus-job-only).
  // escRows renders the printable lines the way Seth writes them: section
  // headers bold, included-in-contract lines say "Included" instead of a
  // wall of $0.00, the Total Contract Price line reads bold with its amount.
  interface EscRow { key: string; qty: number; desc: string; amount: number; header: boolean; included: boolean; total: boolean }
  const escRows: EscRow[] = escNo
    ? (escLines ?? [])
        .filter((l) => !l.no_print && (l.prod ?? "").toUpperCase() !== "DESC" && !["SARES", "SASALES"].includes((l.prod ?? "").toUpperCase()) && !/^payment/i.test((l.line_desc ?? "").trim()) && ((l.line_desc ?? "").trim() !== "" || (l.amount != null && Number(l.amount) !== 0)))
        .map((l) => {
          const desc = (l.line_desc ?? "").trim();
          const qty = Number(l.quan ?? 0);
          const amount = Number(l.amount ?? 0);
          const header = /:$/.test(desc) && amount === 0 && qty === 0;
          const total = (l.prod ?? "").toUpperCase() === "TOTAL" || /total contract price/i.test(desc);
          return { key: l.line_count, qty, desc, amount, header, total, included: !header && !total && amount === 0 && escHasTotalLine };
        })
    : [];

  // FIELD-QUOTES: an undecided proposal from this call rides the PRINTED
  // invoice too — the no-portal path (older customers). Tokenized link, no
  // account, and the phone number as the zero-tech fallback.
  const { data: openProposal } = useQuery({
    queryKey: ["inv_open_proposal", doc?.dispatch_no ?? null],
    enabled: docMode && !!doc?.dispatch_no,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ token: string; title: string | null } | null> => {
      const { data } = await supabase
        .from("pegasus_quote")
        .select("token, title")
        .eq("dispatch_no", doc!.dispatch_no as string)
        .in("status", ["sent", "viewed"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as { token: string; title: string | null } | null) ?? null;
    },
  });

  if ((!docMode && isLoading) || (docMode && docLoading)) return <div className="grid min-h-screen place-items-center text-sm text-slate-400">Loading invoice…</div>;
  if (docMode ? !doc : !realJob) return <div className="grid min-h-screen place-items-center text-sm text-slate-400">Invoice not found.</div>;

  // Synthetic Job shape so the shared layout works whether the source is a job (install)
  // or a unified invoice row (service); service pulls its fields from the invoice doc.
  const job: Job = docMode
    ? ({ id: doc!.id, custno: doc!.custno, locno: doc!.locno, title: doc!.job_name, sale_tech: doc!.comfort_advisor,
        source_dispatch: doc!.dispatch_no, status: "closed", closed_at: doc!.completed_date, sell_price: doc!.total,
        customer_name: null, address: null, job_type: "service" } as unknown as Job)
    : realJob!;

  const closed = job.status === "closed";
  const installISO = (docMode ? (doc!.completed_date ?? doc!.created_at) : job.closed_at) ?? new Date().toISOString();
  const isService = docMode ? doc!.type === "service" : searchParams.get("type") === "service";
  // NC-DRAW-02 / PAY-05: a draw or a deposit is ONE billed line — it prints its
  // line(s), never the job's equipment or change orders.
  const isDraw = docMode && doc!.type === "draw";
  const isLineDoc = docMode && (doc!.type === "draw" || doc!.type === "deposit");
  // PAY-05: deposit/membership documents say "Total" — the "Total Contract Price"
  // label (and its install tagline) belong to install docs only.
  const isInstallDoc = docMode ? doc!.type === "install" : !isService;
  // Design-review Wave-1: once change orders / custom lines move the number,
  // "Total Contract Price" reads wrong (10,700 vs a 9,650 contract) — the
  // customer-facing label becomes plain "Total". Pure-contract installs keep it.
  const hasAddl = docMode
    ? (doc!.lines ?? []).some((l) => l.kind === "change_order" || l.kind === "custom")
    : coAddl + customAddl > 0.005;
  const totalLabel = isInstallDoc && !hasAddl ? "Total Contract Price" : "Total";
  const svcLines = docMode
    ? (doc!.lines ?? []).map((l) => ({ qty: Number(l.qty ?? 1), desc: l.description ?? "", price: Number(l.unit_price ?? 0), waived: l.waived === true }))
    : (isService ? SERVICE_SAMPLE.lines : []);
  // WAIVE-01: a waived line shows its real price struck through and charges $0.
  const svcTotal = svcLines.reduce((s, l) => s + (l.waived ? 0 : l.qty * l.price), 0);
  const total = docMode ? Number(doc!.total ?? svcTotal) : (isService ? svcTotal : (closed && inv?.total != null ? Number(inv.total) : (job.sell_price ?? 0) + coAddl + customAddl));
  // Payment method + reference (card auth / check # / cash / financing) collected by the tech,
  // office, or Stripe. Install (Highland) is unpaid for now; the service EXAMPLE shows a card
  // payment so the method display + "Paid in Full" are visible.
  const payment: Payment | null = docMode
    ? ((doc!.paid_amount ?? 0) > 0 ? { method: doc!.paid_method ?? "", reference: doc!.paid_reference ?? "", amount: Number(doc!.paid_amount ?? 0) } : null)
    : (isService ? SERVICE_SAMPLE.payment : null);
  const tech = docMode ? (doc!.technician ?? null) : (leadInstallerName ?? (labor ?? [])[0]?.crew ?? null);
  const techNotes = docMode ? (doc!.tech_notes ?? "") : SERVICE_SAMPLE.techNotes;
  // PAY-02 (Seth's bug 2026-08-06: row said Paid, document still showed a balance —
  // the THIRD renderer strikes again): the document's payments come from the LEDGER
  // (allocations to this invoice, net of refunds), falling back to the legacy
  // doc.paid_amount single-payment field for old builder invoices.
  // FIELD-PAY-01: one helper for every customer document (docPaymentLines) —
  // the deposit's payment reads "Deposit paid", the installer's "Paid at
  // completion", the lender's "Financing funded". "processing" prints too
  // (PAY-03): a clearing bank payment HOLDS the balance for days.
  const docType = docMode && invoiceId ? ((pegasusDoc as { type?: string | null } | null)?.type ?? null) : null;
  const ledgerPays = payInvoice
    ? docPaymentLines(
        payLedger,
        (a) => a.invoice_id === payInvoice.id || (isVoidDoc && a.applied_from_invoice_id === payInvoice.id),
        (a) => !!a.applied_from_invoice_id || docType === "deposit",
      )
    : preClose && realJob
      ? docPaymentLines(
          payLedger,
          (a) => (a.job_id === realJob.id && !a.invoice_id) || (!!a.invoice_id && depositIds.has(a.invoice_id)),
          (a) => !!a.invoice_id && depositIds.has(a.invoice_id),
        )
      : [];
  const payments = ledgerPays.length > 0
    ? ledgerPays
    : payment ? [{ key: "legacy", at: "", label: "Payment Received", method: payment.method, reference: payment.reference, amount: payment.amount, feeNote: null }] : [];
  const paidTotal = payments.reduce((s, p) => s + p.amount, 0);
  const balance = isVoidDoc ? 0 : payInvoice ? payBalance : Math.max(0, total - paidTotal);
  const paid = !isVoidDoc && balance <= 0.005;
  // Financing approved, lender hasn't paid yet (install documents only).
  const finPending = !isVoidDoc && !isService && !isLineDoc && !paid && jobMoney && jobMoney.pay_plan === "financing"
    && jobMoney.fin_status === "approved" && Number(jobMoney.fin_amount ?? 0) > 0.005
    ? { amount: Number(jobMoney.fin_amount), name: jobMoney.fin_plan_name }
    : null;
  const youOwe = finPending ? Math.max(0, balance - finPending.amount) : balance;
  // Named materials the office marked "prints on invoice" (install) — shown as line items.
  const printedMats = (mats ?? []).filter((m) => m.print);
  // A real assigned number wins (finalized Pegasus number or migrated ESC #); new Pegasus
  // invoices fall back to the dispatch-derived ref (Seth: dispatch # is fine going forward).
  const invNo = docMode
    ? (doc!.invoice_no || `SH-${doc!.dispatch_no ?? doc!.id.slice(0, 6).toUpperCase()}`)
    : ((inv as { invoice_no?: string | null } | null)?.invoice_no || `SH-${job.source_dispatch ?? job.id.slice(0, 6).toUpperCase()}`);
  // Captured by the tech's mobile app at sign-off; renders on the line. Comes off the invoice doc.
  const signatureUrl: string | null = docMode ? (doc!.signature_url ?? null) : null;

  // PROP-STORY-01: an ESC reprint bills the name ESC snapshotted AT INVOICE
  // TIME (ship_name) — on a reused account that's the era-correct owner
  // (Keith's 2024 invoice reprints as Keith, not the account's current name).
  const billName = (escNo ? (escInv?.ship_name ?? "").trim() || null : null)
    ?? cleanName(owner?.fullname ?? null) ?? job.customer_name ?? job.custno;
  const billLine2 = [owner?.add1, owner?.add2].filter(Boolean).join(", ");
  const billLine3 = [owner?.city, owner?.state].filter(Boolean).join(", ") + (owner?.zip ? " " + owner.zip : "");
  // Service location (where the work happened) — real address from mirror_location.
  const svcAddr1 = (isDraw ? (drawJob?.site_address ?? drawJob?.title ?? null) : null) ?? svcLoc?.add1 ?? (docMode ? null : job.address) ?? null;
  const svcAddr2 = isDraw && drawJob
    ? (drawJob.site_city ?? "")
    : [svcLoc?.city, svcLoc?.state].filter(Boolean).join(", ") + (svcLoc?.zip ? " " + svcLoc.zip : "");
  // Envelope mail-to = the owner's billing address; fall back to the service address when
  // no billing address is on file, so the window is never blank.
  const mailAddr1 = billLine2 || svcAddr1 || "";
  const mailAddr2 = (billLine3.trim() ? billLine3 : svcAddr2) || "";
  // INV-LOC-NAME-01 (Seth 2026-10-01: "shouldn't it say the location customer
  // name there? So bold would be Randy Noddings") — the bold line of Service
  // Location is WHO is at the property, matching Bill To's shape; the address
  // drops to regular weight below. The location's own name wins when it's a
  // real name (not just the street repeated); otherwise the account name.
  const svcName = (() => {
    const n = cleanName(svcLoc?.locname ?? null);
    if (!n) return billName;
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
    return svcAddr1 && norm(n) === norm(svcAddr1) ? billName : n;
  })();

  // INV-SERIAL-01: printable equipment for an ESC invoice — grouped by the
  // area each unit serves (matching What's Included), majors before
  // accessories, filters/pads left off, ESC's REAL warranty dates (never a
  // recompute that could contradict the record).
  const ESC_EQ_ORDER = ["furnace", "air-handler", "boiler", "heat-pump", "rooftop", "magic-pak", "garage-heater", "mini-split", "ac", "coil", "water-heater", "erv-hrv", "humidifier", "dehumidifier", "air-cleaner", "uv", "thermostat", "rds"];
  const escEquip = (escEquipRaw ?? [])
    .map((r) => {
      const c = classifyEquipment(r);
      const w = parseWarranty(r);
      const i = ESC_EQ_ORDER.indexOf(c.category);
      return { row: r, tier: c.tier, label: c.categoryLabel, order: i === -1 ? 99 : i, parts: w.parts, labor: w.labor };
    })
    .filter((e) => e.tier !== "material" && e.tier !== "consumable" && (e.row.model || e.row.serial))
    .sort((a, b) => (a.row.equip_loc ?? "").localeCompare(b.row.equip_loc ?? "") || a.order - b.order || (a.row.model ?? "").localeCompare(b.row.model ?? ""));
  const escAreas = [...new Set(escEquip.map((e) => (e.row.equip_loc ?? "").trim()))];
  const escShowAreas = escAreas.filter(Boolean).length >= 2;
  const wd = (d: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" }) : null);

  return (
    <div className="inv-screen">
      {/* screen-only action bar */}
      <div className="inv-actions no-print">
        <div className="inv-toggle" title="Color, or a black-&-white / laser-printer-friendly version">
          <button type="button" className={bw ? "" : "on"} onClick={() => setBw(false)}>Color</button>
          <button type="button" className={bw ? "on" : ""} onClick={() => setBw(true)}>B&amp;W</button>
        </div>
        {canCollect && payInvoice && payInvoice.custno && payBalance > 0.005 && (
          <button type="button" className="inv-btn inv-btn-collect" onClick={() => setShowCollect(true)}
            title="Take a payment on this invoice now">
            <HandCoins size={16} /> Collect {money(payBalance)}
          </button>
        )}
        {/* The SEND lives here by design (Seth 2026-08-11): collect first if the
            customer is paying now, THEN email — the copy reflects the payment;
            unpaid sends carry the balance + portal pay link. REAL now (COMMS
            ARC): goes through the outbox — held while comms is in testing. */}
        {emailInvoice && (
          <>
            {/* Sent to the customer? — read from the outbox, never a flag. */}
            <InvoiceSentChip status={sendStatus} className="mr-1 max-w-[360px]" />
            <button type="button" className="inv-btn inv-btn-ghost" onClick={() => setShowEmail(true)}
              title="Email this invoice — paid copies confirm payment; unpaid copies carry the balance + a pay-online link. Held in the outbox while comms is in testing.">
              <Mail size={16} /> {sendStatus?.state === "sent" ? "Email again" : "Email invoice"}
            </button>
          </>
        )}
        <button type="button" className="inv-btn inv-btn-primary" onClick={() => window.print()}>
          <Printer size={16} /> Print / Save as PDF
        </button>
        <button type="button" className="inv-btn inv-btn-ghost" onClick={closeDoc}>
          <X size={16} /> Close
        </button>
      </div>

      {showCollect && payInvoice && payInvoice.custno && (
        <CollectPaymentModal
          custno={payInvoice.custno}
          customerName={billName}
          invoices={[{
            id: payInvoice.id,
            number: invNo,
            date: null,
            label: payInvoice.type ? payInvoice.type.charAt(0).toUpperCase() + payInvoice.type.slice(1) : "Invoice",
            total: payInvoice.total,
            balance: payBalance,
          }]}
          initialInvoiceId={payInvoice.id}
          onClose={() => setShowCollect(false)}
        />
      )}

      {showEmail && emailInvoice && (
        <SendInvoiceEmailModal
          custno={emailInvoice.custno}
          locno={svcLocno}
          customerName={billName}
          invoiceId={emailInvoice.id}
          invoiceNo={invNo}
          total={emailInvoice.total}
          balance={emailInvoice.balance}
          defaultEmail={emailInvoice.defaultEmail}
          onClose={() => setShowEmail(false)}
        />
      )}

      <article className={"inv-paper" + (bw ? " bw" : "")} ref={paperRef}>
        {/* header band — blue gradient, faceted; Sedgwick logo (graceful wordmark fallback) */}
        <header className="inv-band">
          <svg className="inv-band-geo" viewBox="0 0 900 220" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <polygon points="0,0 300,0 150,220 0,220" fill="rgba(255,255,255,0.035)" />
            <polygon points="300,0 540,0 380,220 150,220" fill="rgba(120,170,255,0.07)" />
            <polygon points="540,0 760,0 600,220 380,220" fill="rgba(255,255,255,0.022)" />
            <polygon points="700,0 900,0 900,220 720,220" fill="rgba(90,140,230,0.08)" />
            <polygon points="815,0 900,0 900,130" fill="rgba(255,255,255,0.045)" />
            <polygon points="120,0 360,0 230,220 40,220" fill="rgba(40,90,180,0.05)" />
          </svg>
          <div className="inv-band-facets" aria-hidden="true" />
          {/* INV-PRINT-POLISH r2 (Seth 2026-08-13): the S mark on a white chip
              (transparent art would sink into the navy band) + disciplined
              single-color type — no gradient word ("don't love the heating in
              a rainbow type color"). The MARK carries the brand color; the
              words carry the authority. Chip hides gracefully if the file
              isn't in /public yet. */}
          <div className="inv-band-left">
            {markOk && (
              <span className="inv-mark-chip">
                <img src="/sedgwick-mark.png" alt="" className="inv-mark" onError={() => setMarkOk(false)} />
              </span>
            )}
            <div>
              <div className="inv-word">SEDGWICK</div>
              <div className="inv-word-sub">HEATING &amp; AIR CONDITIONING</div>
              <div className="inv-tag">{cfg.tagline}</div>
            </div>
          </div>
          <div className="inv-band-right">
            <div className="inv-title">INVOICE</div>
            <div className="inv-subtitle">{isService ? "Service" : isDraw ? "New Construction" : doc?.type === "deposit" ? "Deposit" : "Installation"}</div>
            <div className="inv-meta"><span>Invoice</span><b>{invNo}</b></div>
            <div className="inv-meta"><span>Date</span><b>{fmtDate(installISO)}</b></div>
            {docMode && doc!.po_number && <div className="inv-meta"><span>PO / Job #</span><b>{doc!.po_number}</b></div>}
            {job.source_dispatch && <div className="inv-meta"><span>Dispatch</span><b>{job.source_dispatch}</b></div>}
            {/* INV-AB-01 (the board's Ledger lesson): the money is visible the
                moment the page is — total in the meta, state as a chip. */}
            {total > 0.005 && <div className="inv-meta"><span>Total</span><b>{money(total)}</b></div>}
            {paid && !isVoidDoc && <div className="inv-band-paid">PAID IN FULL</div>}
          </div>
        </header>
        <div className="inv-contact">
          <span>{cfg.addr1}, {cfg.addr2}</span>
          <span className="inv-dot">•</span><span>{cfg.phone}</span>
          <span className="inv-dot">•</span><span>{cfg.web}</span>
        </div>

        {/* parties */}
        <section className="inv-parties">
          {/* Bill To = the window address on a #10 window envelope. Falls back to the
              service address when no separate billing address is on file, so the window
              is never blank. */}
          <div>
            <div className="inv-label">Bill To</div>
            <div className="inv-strong">{billName}</div>
            {mailAddr1 && <div className="inv-soft">{mailAddr1}</div>}
            {mailAddr2.trim() && <div className="inv-soft">{mailAddr2}</div>}
            {owner?.phone1 && <div className="inv-soft">{owner.phone1}</div>}
          </div>
          <div>
            <div className="inv-label">Service Location</div>
            {/* INV-LOC-NAME-01: name bold (who's at the property), address soft. */}
            {svcName && <div className="inv-strong">{svcName}</div>}
            {svcAddr1 && <div className="inv-soft">{svcAddr1}</div>}
            {svcAddr2.trim() && <div className="inv-soft">{svcAddr2}</div>}
            {!svcName && !svcAddr1 && !svcAddr2.trim() && <div className="inv-soft">—</div>}
          </div>
        </section>

        {/* people strip — a service call almost never has a Comfort Advisor, so
            the empty dashed slot is dropped and the strip breathes as 2 columns
            (Seth 2026-08-13). Installs (and the rare advisor-sold service) keep 3. */}
        {!isLineDoc && <section className={"inv-people" + (isService && !(advisorName ?? job.sale_tech) ? " inv-people-2" : "")}>
          {(!isService || Boolean(advisorName ?? job.sale_tech)) && (
            <div><span className="inv-label">Comfort Advisor</span><b>{advisorName ?? job.sale_tech ?? "—"}</b></div>
          )}
          <div><span className="inv-label">{isService ? "Technician" : "Installer"}</span><b>{tech ?? "—"}</b></div>
          <div><span className="inv-label">Completed</span><b>{closed ? fmtDate(job.closed_at) : fmtDate(installISO)}</b></div>
        </section>}

        {/* INV-CUST-01 — an ESC invoice prints its own lines, styled the way
            Seth writes them: bold section headers per system, "Included" on
            the contract-covered lines, the contract line bold. Covers install
            AND service ESC history (service rows carry real amounts). */}
        {escNo && escRows.length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">{isInstallDoc ? "What's Included" : "Work Performed"}</h2>
            <table className="inv-svc-table">
              <thead><tr><th className="inv-r">Qty</th><th>Description</th><th className="inv-r">Amount</th></tr></thead>
              <tbody>
                {escRows.map((r) =>
                  r.header ? (
                    <tr key={r.key}>
                      <td colSpan={3} style={{ paddingTop: 12, fontWeight: 700, letterSpacing: ".01em" }}>{r.desc}</td>
                    </tr>
                  ) : (
                    <tr key={r.key} style={r.total ? { fontWeight: 700 } : undefined}>
                      <td className="inv-r inv-muted">{r.qty > 0 ? (Number.isInteger(r.qty) ? String(r.qty) : r.qty.toFixed(2)) : ""}</td>
                      <td>{r.desc}</td>
                      <td className="inv-r">{r.included ? <span style={{ color: "#8a94a3", fontSize: 11, fontStyle: "italic" }}>Included</span> : money(r.amount)}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
            {techNotes && !isInstallDoc && (
              <div className="inv-notes">
                <div className="inv-notes-label">Technician Notes</div>
                <p>{techNotes}</p>
              </div>
            )}
          </section>
        )}

        {/* service work performed (service invoices); a draw / deposit prints its billed line(s) */}
        {(isService || isLineDoc) && !escNo && (
          <section className="inv-section">
            <h2 className="inv-h2">{isDraw ? `${doc!.label ?? "Draw"} Draw` : isLineDoc ? "Deposit" : "Work Performed"}</h2>
            {isDraw && drawJob?.title && <p className="inv-soft" style={{ margin: "0 0 6px" }}>{drawJob.title}</p>}
            <table className="inv-svc-table">
              <thead><tr><th className="inv-r">Qty</th><th>Description</th><th className="inv-r">Price</th><th className="inv-r">Amount</th></tr></thead>
              <tbody>
                {svcLines.map((l, i) => (
                  <tr key={i}>
                    <td className="inv-r inv-muted">{l.qty.toFixed(2)}</td>
                    <td>{l.desc}{l.waived && <span style={{ color: "#b45309", fontWeight: 600 }}> — waived</span>}</td>
                    <td className="inv-r inv-muted">{l.waived ? <s>{money(l.price)}</s> : money(l.price)}</td>
                    <td className="inv-r">{l.waived ? <><s style={{ color: "#9aa4b1" }}>{money(l.qty * l.price)}</s> $0.00</> : money(l.qty * l.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {techNotes && (
              <div className="inv-notes">
                <div className="inv-notes-label">Technician Notes</div>
                <p>{techNotes}</p>
              </div>
            )}
          </section>
        )}

        {/* INV-SERIAL-01 — the units an ESC invoice installed: model + serial
            per unit (what warranty registration, insurance and inspectors ask
            for), grouped by area like What's Included, with ESC's recorded
            warranty dates. Self-hides when the invoice installed nothing. */}
        {escNo && escEquip.length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">Equipment &amp; Serial Numbers</h2>
            <table className="inv-table inv-table-center">
              <thead>
                <tr><th>Equipment</th><th>Model</th><th>Serial #</th><th>Parts warranty</th><th>Labor warranty</th></tr>
              </thead>
              <tbody>
                {escAreas.map((area) => (
                  <Fragment key={area || "unassigned"}>
                    {escShowAreas && area && (
                      <tr>
                        <td colSpan={5} style={{ paddingTop: 10, fontWeight: 700, textAlign: "left", letterSpacing: ".01em" }}>{area}</td>
                      </tr>
                    )}
                    {escEquip.filter((e) => (e.row.equip_loc ?? "").trim() === area).map((e) => (
                      <tr key={e.row.counter}>
                        <td>{e.label}{e.row.mfg ? ` · ${prettyBrand(e.row.mfg)}` : ""}</td>
                        <td className="inv-mono">{e.row.model ?? "—"}</td>
                        <td className="inv-mono inv-muted">{e.row.serial ?? "—"}</td>
                        <td className="inv-muted">{wd(e.parts) ?? "—"}</td>
                        <td className="inv-muted">{wd(e.labor) ?? "—"}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* equipment block (install only) */}
        {!isService && !isLineDoc && visEquip.length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">Equipment Installed</h2>
            <table className="inv-table inv-table-center">
              <thead>
                <tr><th>Manufacturer</th><th>Model</th><th>Serial #</th><th>Parts warranty</th><th>Labor warranty</th></tr>
              </thead>
              <tbody>
                {visEquip.map((e) => (
                  <tr key={e.id}>
                    <td>{manufacturerLabel(e)}</td>
                    <td className="inv-mono">{e.model ?? "—"}</td>
                    <td className="inv-mono inv-muted">{e.serial ?? "—"}</td>
                    <td className="inv-muted">{warranty(e.category, installISO, "parts", cfg)}</td>
                    <td className="inv-muted">{warranty(e.category, installISO, "labor", cfg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* materials & accessories provided (install) — named line items the office marked "prints on invoice" */}
        {!isService && !isLineDoc && printedMats.filter((m) => !hiddenKeys.has(`material:${m.id}`)).length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">Materials &amp; Accessories</h2>
            <table className="inv-table">
              <tbody>
                {printedMats.filter((m) => !hiddenKeys.has(`material:${m.id}`)).map((m) => {
                  const q = m.qty_actual ?? m.qty;
                  return (
                    <tr key={m.id}>
                      <td>{labelFor(`material:${m.id}`, m.description ?? "")}</td>
                      <td className="inv-r inv-muted">{q != null && Number(q) > 1 ? `×${q}${m.unit ? " " + m.unit : ""}` : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* approved change orders — added scope + cost, disclosed on the customer invoice.
            Renders in docMode too (2026-08-06): the finalized snapshot total now includes
            these, so the durable /invoices/:id view must list them as well. */}
        {!isService && !isLineDoc && visCOs.length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">Change Orders</h2>
            <table className="inv-table">
              <tbody>
                {visCOs.map((c) => (
                  <tr key={c.id}>
                    <td>{labelFor(`co:${c.id}`, c.description ?? "Change order")}</td>
                    <td className="inv-r">{money(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* office-added one-off custom lines (invoice customization overlay).
            Renders in docMode too (2026-08-06) — same parity reason as change orders. */}
        {!isService && !isLineDoc && customLines.length > 0 && (
          <section className="inv-section">
            <h2 className="inv-h2">Additional</h2>
            <table className="inv-table">
              <tbody>
                {customLines.map((c) => (
                  <tr key={c.id}>
                    <td>{c.label}</td>
                    <td className="inv-r">{c.amount != null ? money(c.amount) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* customer note / install description — ABOVE the price (Seth): a description or a heads-up */}
        {!isService && !isLineDoc && job.invoice_message && (
          <section className="inv-section">
            <div className="inv-msg">
              <div className="inv-msg-label">A Note From Sedgwick</div>
              <p>{job.invoice_message}</p>
            </div>
          </section>
        )}

        {/* financials — one clean box: contract price → any payments → balance due */}
        <section className="inv-section">
          <div className="inv-fin">
            <div className="inv-fin-row inv-fin-total">
              <div>
                <div className="inv-fin-label">{totalLabel}</div>
                {isInstallDoc && <div className="inv-fin-note">Complete, professionally installed comfort system</div>}
              </div>
              <div className="inv-fin-amt">{money(total)}</div>
            </div>
            {payments.map((p) => (
              <div key={p.key} className="inv-fin-row inv-fin-pay">
                <span>
                  {p.label}{p.method ? ` — ${p.method}` : ""}{p.reference ? ` · ${p.reference}` : ""}
                  {p.feeNote && <span className="inv-fin-fee">{p.feeNote}</span>}
                </span>
                <b>−{money(p.amount)}</b>
              </div>
            ))}
            {!isVoidDoc && (paid || !escNo || escRecKnown) && (
              <div className="inv-fin-row inv-fin-balance">
                <span>{paid ? "Paid in Full" : "Balance Due"}</span>
                <b>{money(balance)}</b>
              </div>
            )}
            {finPending && (
              <div className="inv-fin-row inv-fin-pay">
                <span>Financing approved{finPending.name ? ` — ${finPending.name}` : ""} · the lender pays this part</span>
                <b>{money(finPending.amount)}</b>
              </div>
            )}
          </div>
          {isVoidDoc ? (
            <div className="inv-pay"><span className="inv-due-note">{docType === "deposit" ? "This deposit is part of your final invoice — it isn't owed separately." : "This invoice was voided — nothing is owed on it."}</span></div>
          ) : paid ? (
            <div className="inv-pay"><span className="inv-paid-badge"><CheckCircle2 size={15} /> Paid in Full — Thank You</span></div>
          ) : finPending && youOwe <= 0.005 ? (
            <div className="inv-pay"><span className="inv-due-note">Nothing is due from you — your financing covers the balance.</span></div>
          ) : (escNo && !escRecKnown) ? (
            <div className="inv-pay"><span className="inv-due-note">Balance and any payments applied are maintained on your Sedgwick account.</span></div>
          ) : (
            <div className="inv-pay">
              <span className="inv-due-note">{finPending ? `${money(youOwe)} is due from you upon completion` : "Due upon completion"}</span>
              <a className="inv-pay-btn" href="https://sedgwickheating.com/pay" target="_blank" rel="noreferrer">Pay Online →</a>
            </div>
          )}
        </section>

        {/* Undecided proposal — prints AND emails with the invoice so no one
            misses their options, portal or not. */}
        {docMode && openProposal && (
          <section style={{ marginTop: 16, padding: "12px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10 }}>
            <div style={{ fontSize: "12.5px", fontWeight: 700, color: "#92400e" }}>
              Your options from this visit are ready to review{openProposal.title ? ` — ${openProposal.title}` : ""}
            </div>
            <div style={{ fontSize: "11.5px", color: "#b45309", marginTop: 3, lineHeight: 1.5 }}>
              Open this link any time — no account needed:{" "}
              <a href={`/proposal?t=${openProposal.token}`} style={{ color: "#b45309", fontWeight: 700, wordBreak: "break-all" }}>
                {`${window.location.origin}/proposal?t=${openProposal.token}`}
              </a>
            </div>
            <div style={{ fontSize: "11.5px", color: "#b45309", marginTop: 3 }}>
              Prefer the phone? Call (952) 881-9000 — we'll walk through the options and can approve your choice for you.
            </div>
          </section>
        )}

        {/* customer completion signature — captured by the installer's mobile close-out; shown only when signed */}
        {signatureUrl && (
        <section className="inv-sign">
          <div>
            {signatureUrl
              ? <img src={signatureUrl} alt="Customer signature" className="inv-sign-img" />
              : <div className="inv-sign-rule" />}
            <div className="inv-sign-label">Customer Signature</div>
          </div>
          <div>
            <div className="inv-sign-rule" />
            <div className="inv-sign-label">Date</div>
          </div>
          <p className="inv-sign-auth">{isService
            ? <>Customer acknowledges the work described above was completed to their satisfaction. Full terms at {cfg.web}/terms.</>
            : <>Customer acknowledges the equipment above was installed and is operating properly. Warranty coverage is as listed above; full terms at {cfg.web}/terms.</>}</p>
        </section>
        )}

        {/* footer */}
        <footer className="inv-footer">
          <div className="inv-foot-accent" />
          <div className="inv-badge-wrap">
            <span className="inv-badge"><Heart size={13} fill="currentColor" /> {cfg.badge}</span>
          </div>
          <div className="inv-foot-grid">
            <div>
              <div className="inv-foot-strong">Thank you for choosing Sedgwick Heating.</div>
              <div className="inv-soft">Pay online at <a className="inv-link" href="https://sedgwickheating.com/pay" target="_blank" rel="noreferrer">{cfg.pay}</a></div>
            </div>
            <div className="inv-foot-right inv-soft">
              <div className="inv-foot-strong">{cfg.name}</div>
              <div>Call {cfg.phone} · Text {cfg.textPhone}</div>
              <div>{cfg.email} · {cfg.web}</div>
              <div>{cfg.license}</div>
            </div>
          </div>
          <div className="inv-terms">See our full terms and conditions at <a className="inv-link" href={`https://${cfg.web}/terms`} target="_blank" rel="noreferrer">{cfg.web}/terms</a></div>
        </footer>
      </article>

      <style>{`
        :root { --ink:#0f172a; --accent:#b1271b; --muted:#64748b; --hair:#e5e7eb; --soft:#475569; }
        /* INV-AB-01 glass frame (SCREEN ONLY — print resets to white below):
           the paper floats on a soft lit canvas; the document itself stays
           pure ink so the PDF and the laser copy never change. */
        .inv-screen { min-height:100vh; padding:28px 16px 80px;
          background:
            radial-gradient(1100px 340px at 18% -5%, #dbe7f6 0%, rgba(219,231,246,0) 60%),
            radial-gradient(900px 420px at 95% 100%, #e8e2f4 0%, rgba(232,226,244,0) 55%),
            #eef1f5; }
        /* INV-PRINT-POLISH: the toolbar rides ABOVE the paper (sticky), never
           floats over it — on narrow windows the fixed version overlapped the
           invoice header (Seth 2026-08-13). INV-AB-01: it becomes a frosted
           capsule — the one place the glass look lives. */
        .inv-actions { position:sticky; top:10px; z-index:10; display:flex; flex-wrap:wrap; gap:10px; justify-content:flex-end; align-items:center;
          max-width:850px; margin:0 auto 14px; padding:8px 10px;
          background:rgba(255,255,255,.55); -webkit-backdrop-filter:blur(10px); backdrop-filter:blur(10px);
          border:1px solid rgba(255,255,255,.75); border-radius:999px; box-shadow:0 10px 30px -14px rgba(30,50,90,.28); }
        .inv-btn { display:inline-flex; align-items:center; gap:7px; border-radius:999px; padding:9px 15px; font-size:13px; font-weight:600; cursor:pointer; border:1px solid transparent; }
        .inv-btn-primary { background:var(--ink); color:#fff; box-shadow:0 6px 18px rgba(15,23,42,.22); }
        /* The money action stands apart (Seth 2026-08-11: make Collect impossible
           to miss on an open invoice) — emerald, larger, carries the amount. */
        .inv-btn-collect { background:#047857; color:#fff; box-shadow:0 6px 18px rgba(4,120,87,.32); font-size:14px; padding:11px 18px; }
        .inv-btn-collect:hover { background:#065f46; }
        .inv-btn-primary:hover { background:#1e293b; }
        .inv-btn-ghost { background:rgba(255,255,255,.85); color:var(--soft); border-color:rgba(226,232,240,.9); }
        .inv-btn-ghost:hover { background:#fff; }
        .inv-toggle { display:inline-flex; background:rgba(255,255,255,.85); border:1px solid rgba(226,232,240,.9); border-radius:999px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,.06); }
        .inv-toggle button { border:none; background:#fff; color:var(--soft); font-size:12px; font-weight:600; padding:9px 14px; cursor:pointer; }
        .inv-toggle button.on { background:var(--ink); color:#fff; }

        /* ── Black-&-white / laser-printer mode — crisp black on white, no muddy gradients ── */
        .inv-paper.bw { color:#111; }
        .inv-paper.bw .inv-band { background:#fff !important; color:#111; border-bottom:2.5px solid #111; }
        .inv-paper.bw .inv-band-facets, .inv-paper.bw .inv-band-geo { display:none !important; }
        .inv-paper.bw .inv-title { color:#111; }
        .inv-paper.bw .inv-subtitle { color:#444; }
        .inv-paper.bw .inv-meta { border-top-color:#d1d5db; }
        .inv-paper.bw .inv-meta span { color:#555; } .inv-paper.bw .inv-meta b { color:#111; }
        .inv-paper.bw .inv-word { color:#111; }
        .inv-paper.bw .inv-word-sub, .inv-paper.bw .inv-tag { color:#444; }
        .inv-paper.bw .inv-mark-chip { box-shadow:none; border:1px solid #d1d5db; }
        .inv-paper.bw .inv-mark { filter:grayscale(1); }
        .inv-paper.bw .inv-fin { background:#fff !important; border-color:#111; border-left-color:#111; }
        .inv-paper.bw .inv-fin-pay, .inv-paper.bw .inv-fin-balance { background:#fff !important; }
        .inv-paper.bw .inv-fin-balance { border-top-color:#111; }
        .inv-paper.bw .inv-people, .inv-paper.bw .inv-contact, .inv-paper.bw .inv-foot-grid, .inv-paper.bw .inv-terms, .inv-paper.bw .inv-msg { background:#fff !important; }
        .inv-paper.bw .inv-people, .inv-paper.bw .inv-msg { border-color:#d1d5db; }
        .inv-paper.bw .inv-h2 { border-bottom-color:#111; color:#111; }
        .inv-paper.bw .inv-band-paid { background:#fff !important; border-color:#111; color:#111; }
        .inv-paper.bw .inv-foot-accent { background:#111; }
        .inv-paper.bw .inv-pay-btn { background:#fff !important; color:#111 !important; border:1.5px solid #111; box-shadow:none; }
        .inv-paper.bw .inv-paid-badge { background:#fff !important; color:#111 !important; border:1.5px solid #111; }
        .inv-paper.bw .inv-check { color:#111 !important; }
        .inv-paper.bw a, .inv-paper.bw .inv-link { color:#111 !important; }
        .inv-paper.bw .inv-badge { color:#222; border-color:#9ca3af; }
        .inv-paper.bw .inv-badge svg { color:#222; }

        .inv-paper { max-width:850px; margin:0 auto; background:#fff; color:var(--ink);
          border-radius:14px; box-shadow:0 12px 40px rgba(15,23,42,.12); overflow:hidden;
          font-family:'Inter',ui-sans-serif,system-ui,-apple-system,sans-serif; }

        .inv-band { position:relative; overflow:hidden; color:#fff; padding:30px 44px 28px; display:flex; justify-content:space-between; align-items:center; gap:24px;
          background:linear-gradient(125deg,#163a66 0%,#0f2747 48%,#0a172e 100%); }
        .inv-band-facets { position:absolute; inset:0; pointer-events:none; opacity:.55; mix-blend-mode:screen;
          background-image:
            linear-gradient(118deg, rgba(120,170,255,.22) 0%, rgba(120,170,255,0) 34%),
            linear-gradient(300deg, rgba(70,120,210,.20) 0%, rgba(70,120,210,0) 40%),
            linear-gradient(168deg, rgba(255,255,255,.06) 0%, rgba(255,255,255,0) 30%); }
        .inv-band-left, .inv-band-right { position:relative; z-index:1; }
        .inv-band-geo { position:absolute; inset:0; width:100%; height:100%; z-index:0; pointer-events:none; }
        .inv-band-left { display:flex; align-items:center; gap:20px; }
        /* App-icon proportions (Seth 2026-08-13: "a little taller so more
           square") — a near-square chip with the wide S centered in it. */
        .inv-mark-chip { display:inline-flex; align-items:center; justify-content:center; background:#fff;
          width:110px; height:92px; border-radius:18px; box-shadow:0 6px 18px rgba(0,0,0,.28); }
        .inv-mark { width:84px; height:auto; display:block; }
        .inv-word { font-size:31px; font-weight:800; letter-spacing:.13em; line-height:1; color:#fff; }
        .inv-word-sub { font-size:12.5px; font-weight:600; letter-spacing:.27em; color:#c7d5ea; margin-top:7px; }
        .inv-tag { font-size:12px; color:#94a3b8; margin-top:11px; letter-spacing:.02em; }
        .inv-band-right { text-align:right; min-width:210px; }
        .inv-title { font-size:30px; font-weight:300; letter-spacing:.22em; color:#fff; margin-bottom:2px; }
        .inv-subtitle { font-size:10.5px; font-weight:600; letter-spacing:.26em; text-transform:uppercase; color:#9fb4d6; margin-bottom:12px; }
        .inv-meta { display:flex; justify-content:space-between; gap:18px; font-size:12px; padding:2px 0; border-top:1px solid rgba(255,255,255,.1); }
        .inv-meta span { color:#94a3b8; } .inv-meta b { color:#fff; font-weight:600; }
        /* INV-AB-01: paid state right in the band — solid-ink friendly. */
        .inv-band-paid { display:inline-block; margin-top:8px; padding:3px 12px; border-radius:999px;
          background:rgba(16,185,129,.18); border:1px solid rgba(110,231,183,.45); color:#6ee7b7;
          font-size:10.5px; font-weight:800; letter-spacing:.14em; }

        .inv-contact { display:flex; flex-wrap:wrap; gap:8px; justify-content:center; align-items:center;
          background:#f8fafc; border-bottom:1px solid var(--hair); padding:9px 44px; font-size:12px; color:var(--muted); }
        .inv-dot { color:#cbd5e1; }

        .inv-parties { display:grid; grid-template-columns:1fr 1fr; gap:28px; padding:28px 44px 6px; }
        .inv-label { font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:var(--muted); margin-bottom:5px; }
        .inv-strong { font-size:15.5px; font-weight:700; color:var(--ink); }
        .inv-soft { font-size:13px; color:var(--soft); line-height:1.5; }

        .inv-people { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin:18px 44px 4px; padding:14px 20px;
          background:#f8fafc; border:1px solid var(--hair); border-radius:10px; }
        .inv-people-2 { grid-template-columns:repeat(2,1fr); }
        .inv-people > div { display:flex; flex-direction:column; gap:3px; }
        .inv-people b { font-size:14px; font-weight:600; color:var(--ink); }

        .inv-section { padding:22px 44px 0; }
        /* INV-AB-01 (Ledger internals): section heads lose the heavy ink bar —
           a hairline carries the eye; the HEADING is the weight. */
        .inv-h2 { font-size:12px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--ink);
          padding-bottom:8px; border-bottom:1px solid var(--hair); margin-bottom:0; }
        .inv-h2-mt { margin-top:26px; }

        .inv-table { width:100%; border-collapse:collapse; font-size:13px; }
        .inv-table th { text-align:left; font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase;
          color:var(--muted); padding:10px 10px 8px; border-bottom:1px solid var(--hair); }
        .inv-table td { padding:11px 10px; border-bottom:1px solid #f1f5f9; color:var(--ink); }
        .inv-table tr:last-child td { border-bottom:none; }
        .inv-mono { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:11.5px; }
        .inv-muted { color:var(--muted); } .inv-r { text-align:right; }
        .inv-table-center th, .inv-table-center td { text-align:center; }

        .inv-hero { display:flex; justify-content:space-between; align-items:center; gap:20px; margin-top:6px;
          background:linear-gradient(180deg,#f8fafc,#f1f5f9); border:1px solid var(--hair); border-left:4px solid var(--accent);
          border-radius:12px; padding:20px 26px; }
        .inv-hero-label { font-size:13px; font-weight:700; color:var(--ink); }
        .inv-hero-note { font-size:11.5px; color:var(--muted); margin-top:3px; }
        .inv-hero-amt { font-size:30px; font-weight:800; letter-spacing:-.01em; color:var(--ink); }

        /* Financials box — Total Contract Price → payments → balance, one clean unit. */
        .inv-fin { border:1px solid var(--hair); border-left:4px solid var(--accent); border-radius:12px; overflow:hidden; background:linear-gradient(180deg,#f8fafc,#f1f5f9); }
        .inv-fin-row { display:flex; justify-content:space-between; align-items:center; gap:20px; padding:16px 26px; }
        .inv-fin-label { font-size:13px; font-weight:700; color:var(--ink); }
        .inv-fin-note { font-size:11.5px; color:var(--muted); margin-top:3px; }
        .inv-fin-amt { font-size:28px; font-weight:800; letter-spacing:-.01em; color:var(--ink); }
        .inv-fin-pay { padding:9px 26px; font-size:12.5px; color:var(--soft); border-top:1px solid var(--hair); background:#fff; }
        .inv-fin-pay b { font-weight:600; color:var(--ink); }
        .inv-fin-fee { display:block; margin-top:2px; font-size:11px; font-weight:400; color:var(--muted); }
        .inv-fin-balance { border-top:2px solid var(--ink); background:#fff; }
        .inv-fin-balance span { font-size:15px; font-weight:700; color:var(--ink); }
        .inv-fin-balance b { font-size:20px; font-weight:800; color:var(--ink); }
        .inv-msg { border:1px solid var(--hair); border-radius:10px; background:#f8fafc; padding:13px 20px; }
        .inv-msg-label { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--muted); margin-bottom:5px; }
        .inv-msg p { font-size:12.5px; line-height:1.55; color:var(--soft); margin:0; white-space:pre-wrap; }

        .inv-incl { list-style:none; margin:14px 0 0; padding:0; display:grid; grid-template-columns:1fr 1fr; gap:9px 26px; }
        .inv-incl li { display:flex; align-items:flex-start; gap:9px; font-size:12.5px; color:var(--soft); }
        .inv-check { color:var(--accent); flex:0 0 auto; margin-top:1px; }

        .inv-totals { padding:26px 44px 0; margin-top:8px; }
        .inv-tline { display:flex; justify-content:flex-end; gap:40px; align-items:baseline; font-size:13px; color:var(--soft); padding:5px 0; }
        .inv-tline b { min-width:120px; text-align:right; font-weight:600; color:var(--ink); }
        .inv-due { border-top:2px solid var(--ink); margin-top:6px; padding-top:12px; font-size:16px; }
        .inv-due span { font-weight:700; color:var(--ink); } .inv-due b { font-size:20px; font-weight:800; }
        .inv-due-note { text-align:right; font-size:11.5px; color:var(--muted); margin-top:6px; }
        .inv-hist-note { border-top:1px dashed var(--hair); margin-top:8px; padding-top:10px; justify-content:flex-end; }
        .inv-hist-note span { font-size:11.5px; color:var(--muted); font-style:italic; }
        .inv-pay { display:flex; align-items:center; justify-content:flex-end; gap:14px; margin-top:14px; flex-wrap:wrap; }
        .inv-pay .inv-due-note { margin-top:0; }
        .inv-pay-btn { display:inline-flex; align-items:center; gap:6px; background:var(--accent); color:#fff; font-weight:700; font-size:13px; padding:10px 20px; border-radius:9px; text-decoration:none; box-shadow:0 4px 12px rgba(177,39,27,.25); }
        .inv-pay-btn:hover { filter:brightness(1.06); }
        .inv-paid-badge { display:inline-flex; align-items:center; gap:7px; background:#ecfdf5; color:#047857; border:1px solid #a7f3d0; border-radius:9px; padding:9px 18px; font-size:13px; font-weight:700; }
        .inv-link { color:var(--accent); text-decoration:none; font-weight:600; }

        .inv-footer { margin-top:30px; }
        .inv-foot-accent { height:4px; background:var(--accent); }
        .inv-foot-grid { display:flex; justify-content:space-between; gap:20px; padding:18px 44px 30px; background:#f8fafc; }
        .inv-foot-strong { font-size:13px; font-weight:700; color:var(--ink); }
        .inv-foot-right { text-align:right; line-height:1.6; }
        .inv-badge-wrap { display:flex; justify-content:center; padding:18px 0; }
        .inv-badge { display:inline-flex; align-items:center; gap:8px; border:1px solid var(--hair); background:#fff; border-radius:999px; padding:8px 18px; font-size:10.5px; font-weight:700; letter-spacing:.09em; color:#475569; box-shadow:0 2px 8px rgba(15,23,42,.05); }
        .inv-badge svg { color:var(--accent); }

        .inv-svc-table { width:100%; border-collapse:collapse; font-size:13px; margin-top:4px; }
        .inv-svc-table th { font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); padding:9px 10px; border-bottom:1px solid var(--hair); text-align:left; }
        .inv-svc-table td { padding:9px 10px; border-bottom:1px solid #f1f5f9; color:var(--ink); vertical-align:top; }
        .inv-notes { margin-top:16px; }
        .inv-notes-label { font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); margin-bottom:4px; }
        .inv-notes p { font-size:12.5px; line-height:1.55; color:var(--soft); margin:0; white-space:pre-line; }
        .inv-sign { padding:26px 44px 6px; display:grid; grid-template-columns:2fr 1fr; gap:8px 28px; align-items:end; }
        .inv-sign-rule { border-bottom:1.5px solid #94a3b8; height:36px; }
        .inv-sign-img { height:40px; width:auto; max-width:100%; display:block; border-bottom:1.5px solid #94a3b8; padding-bottom:2px; }
        .inv-sign-label { font-size:9.5px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--muted); margin-top:5px; }
        .inv-sign-auth { grid-column:1 / -1; font-size:10px; line-height:1.5; color:var(--muted); margin:10px 0 0; }
        .inv-terms { text-align:center; font-size:10.5px; color:var(--muted); padding:12px 44px 20px; background:#f8fafc; border-top:1px solid var(--hair); }
        .inv-terms .inv-link { font-weight:600; }
        @media print {
          /* Small uniform margin. Office printers (e.g. the RICOH IM3000) CANNOT print to
             the very edge, so margin:0 makes them clip content (the AMOUNT column, totals).
             0.25in is about the smallest safe border that keeps everything on the sheet. */
          @page { size:letter; margin:0; }
          .inv-screen { background:#fff; padding:0; min-height:0; }
          .no-print { display:none !important; }
          .inv-paper { max-width:none; width:100%; margin:0; box-shadow:none; border-radius:0; }
          .inv-band, .inv-band-facets, .inv-band-geo, .inv-hero, .inv-foot-accent, .inv-check, .inv-h2, .inv-pay-btn, .inv-paid-badge, .inv-mark-chip, .inv-badge svg { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
          /* Colored backgrounds fill the sheet; a modest text inset keeps rows readable. */
          .inv-section, .inv-parties, .inv-totals, .inv-sign,
          .inv-band, .inv-contact, .inv-people, .inv-foot-grid, .inv-terms { padding-left:0.3in; padding-right:0.3in; }
        }
        /* One-page tuning (2026-07-02): tighter vertical rhythm (screen + print) so the auto-fit
           shrinks far less and the invoice reads near full-size on a single Letter page. Vertical
           only — horizontal padding + the print inset above are untouched. */
        .inv-band { padding-top:16px; padding-bottom:14px; }
        .inv-word { font-size:25px; }
        .inv-mark { width:64px; }
        .inv-mark-chip { width:84px; height:70px; border-radius:14px; }
        .inv-contact { padding-top:6px; padding-bottom:6px; }
        .inv-parties { padding-top:14px; padding-bottom:2px; }
        .inv-people { margin-top:10px; margin-bottom:0; padding-top:9px; padding-bottom:9px; }
        .inv-section { padding-top:13px; }
        .inv-h2-mt { margin-top:12px; }
        .inv-table td { padding-top:6px; padding-bottom:6px; }
        .inv-table th { padding-top:7px; padding-bottom:6px; }
        .inv-hero { padding-top:13px; padding-bottom:13px; }
        .inv-incl { margin-top:9px; row-gap:5px; }
        .inv-totals { padding-top:12px; margin-top:2px; }
        .inv-tline { padding-top:3px; padding-bottom:3px; }
        .inv-sign { padding-top:12px; padding-bottom:2px; }
        .inv-footer { margin-top:10px; }
        .inv-badge-wrap { padding-top:6px; padding-bottom:6px; }
        .inv-foot-grid { padding-top:10px; padding-bottom:12px; }
        .inv-terms { padding-top:6px; padding-bottom:10px; }
      `}</style>
    </div>
  );
}
